import { createClient } from '@supabase/supabase-js';

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Check if Supabase is properly configured
export const isSupabaseConfigured = () => {
  const isConfigured = supabaseUrl && 
         supabaseAnonKey && 
         supabaseUrl !== 'https://your-project-ref.supabase.co' &&
         supabaseAnonKey !== 'your-anon-key-here' &&
         supabaseUrl.includes('supabase.co') &&
         supabaseUrl.startsWith('https://') &&
         supabaseAnonKey.length > 20; // Basic validation for anon key length
  
  if (!isConfigured) {
    console.warn('⚠️ Supabase Configuration Required');
    console.warn('Please set up your Supabase credentials in the .env file:');
    console.warn('1. VITE_SUPABASE_URL=https://your-project-ref.supabase.co');
    console.warn('2. VITE_SUPABASE_ANON_KEY=your-anon-key-here');
    console.warn('Visit https://supabase.com to create a free account and get your credentials.');
  }
  
  return isConfigured;
};

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('❌ Supabase URL or Anon Key is missing. Please check your .env file');
}

// Create Supabase client with error handling
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Database types
export interface Database {
  public: {
    Tables: {
      products: {
        Row: {
          id: string;
          name: string;
          category: string;
          description: string;
          image: string;
          videoUrl: string | null;
          price: number | null;
          features: string[];
          is_featured: boolean;
          status: 'published' | 'draft';
          created_at: string;
          updated_at: string;
          related_page_slugs: string[];
        };
        Insert: {
          id?: string;
          name: string;
          category: string;
          description: string;
          image: string;
          videoUrl?: string | null;
          price?: number | null;
          features?: string[];
          is_featured?: boolean;
          status?: 'published' | 'draft';
          created_at?: string;
          updated_at?: string;
          related_page_slugs?: string[];
        };
        Update: {
          id?: string;
          name?: string;
          category?: string;
          description?: string;
          image?: string;
          videoUrl?: string | null;
          price?: number | null;
          features?: string[];
          is_featured?: boolean;
          status?: 'published' | 'draft';
          updated_at?: string;
          related_page_slugs?: string[];
        };
      };
      pages: {
        Row: {
          id: string;
          title: string;
          slug: string;
          content: string;
          meta_title: string;
          meta_description: string;
          status: 'published' | 'draft';
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          title: string;
          slug: string;
          content: string;
          meta_title?: string;
          meta_description?: string;
          status?: 'published' | 'draft';
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          title?: string;
          slug?: string;
          content?: string;
          meta_title?: string;
          meta_description?: string;
          status?: 'published' | 'draft';
          updated_at?: string;
        };
      };
      blog_posts: {
        Row: {
          id: string;
          title: string;
          slug: string;
          content: string;
          excerpt: string | null;
          featured_image: string | null;
          author_id: string | null;
          categories: string[];
          tags: string[];
          seo_title: string | null;
          seo_description: string | null;
          focus_keyword: string | null;
          canonical_url: string | null;
          status: 'published' | 'draft';
          published_at: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          title: string;
          slug: string;
          content: string;
          excerpt?: string | null;
          featured_image?: string | null;
          author_id?: string | null;
          categories?: string[];
          tags?: string[];
          seo_title?: string | null;
          seo_description?: string | null;
          focus_keyword?: string | null;
          canonical_url?: string | null;
          status?: 'published' | 'draft';
          published_at?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          title?: string;
          slug?: string;
          content?: string;
          excerpt?: string | null;
          featured_image?: string | null;
          author_id?: string | null;
          categories?: string[];
          tags?: string[];
          seo_title?: string | null;
          seo_description?: string | null;
          focus_keyword?: string | null;
          canonical_url?: string | null;
          status?: 'published' | 'draft';
          published_at?: string | null;
          updated_at?: string;
        };
      };
      inquiries: {
        Row: {
          id: string;
          name: string;
          email: string;
          phone: string;
          company: string | null;
          product: string | null;
          quantity: string | null;
          location: string | null;
          message: string;
          status: 'new' | 'read' | 'replied';
          created_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          email: string;
          phone: string;
          company?: string | null;
          product?: string | null;
          quantity?: string | null;
          location?: string | null;
          message: string;
          status?: 'new' | 'read' | 'replied';
          created_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          email?: string;
          phone?: string;
          company?: string | null;
          product?: string | null;
          quantity?: string | null;
          location?: string | null;
          message?: string;
          status?: 'new' | 'read' | 'replied';
        };
      };
      leads: {
        Row: {
          id: string;
          mobile: string;
          product: string | null;
          quantity: string | null;
          status: 'new' | 'contacted' | 'converted';
          source: string;
          created_at: string;
        };
        Insert: {
          id?: string;
          mobile: string;
          product?: string | null;
          quantity?: string | null;
          status?: 'new' | 'contacted' | 'converted';
          source?: string;
          created_at?: string;
        };
        Update: {
          id?: string;
          mobile?: string;
          product?: string | null;
          quantity?: string | null;
          status?: 'new' | 'contacted' | 'converted';
          source?: string;
        };
      };
      plugins: {
        Row: {
          id: string;
          name: string;
          description: string;
          version: string;
          author: string;
          is_active: boolean;
          is_installed: boolean;
          created_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          description: string;
          version: string;
          author: string;
          is_active?: boolean;
          is_installed?: boolean;
          created_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          description?: string;
          version?: string;
          author?: string;
          is_active?: boolean;
          is_installed?: boolean;
        };
      };
      themes: {
        Row: {
          id: string;
          name: string;
          description: string;
          version: string;
          author: string;
          is_active: boolean;
          preview: string;
          created_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          description: string;
          version: string;
          author: string;
          is_active?: boolean;
          preview: string;
          created_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          description?: string;
          version?: string;
          author?: string;
          is_active?: boolean;
          preview?: string;
        };
      };
      redirects: {
        Row: {
          id: string;
          old_url: string;
          new_url: string;
          type: '301' | '302' | '307';
          is_active: boolean;
          created_at: string;
        };
        Insert: {
          id?: string;
          old_url: string;
          new_url: string;
          type?: '301' | '302' | '307';
          is_active?: boolean;
          created_at?: string;
        };
        Update: {
          id?: string;
          old_url?: string;
          new_url?: string;
          type?: '301' | '302' | '307';
          is_active?: boolean;
        };
      };
      site_settings: {
        Row: {
          id: string;
          key: string;
          value: any;
          updated_at: string;
        };
        Insert: {
          id?: string;
          key: string;
          value?: any;
          updated_at?: string;
        };
        Update: {
          id?: string;
          key?: string;
          value?: any;
          updated_at?: string;
        };
      };
      video_library: {
        Row: {
          id: string;
          name: string;
          url: string;
          thumbnail?: string;
          duration?: number;
          size: number;
          file_type: string;
          alt?: string;
          caption?: string;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          url: string;
          thumbnail?: string;
          duration?: number;
          size: number;
          file_type: string;
          alt?: string;
          caption?: string;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          url?: string;
          thumbnail?: string;
          duration?: number;
          size?: number;
          file_type?: string;
          alt?: string;
          caption?: string;
          updated_at?: string;
        };
      };
      image_library: {
        Row: {
          id: string;
          name: string;
          url: string;
          thumbnail?: string;
          size: number;
          file_type: string;
          alt?: string;
          caption?: string;
          width?: number;
          height?: number;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          url: string;
          thumbnail?: string;
          size: number;
          file_type: string;
          alt?: string;
          caption?: string;
          width?: number;
          height?: number;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          url?: string;
          thumbnail?: string;
          size?: number;
          file_type?: string;
          alt?: string;
          caption?: string;
          width?: number;
          height?: number;
          updated_at?: string;
        };
      };
    };
  };
}